import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiLock, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState('');
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const resetToken = searchParams.get('token');
    if (resetToken) {
      setToken(resetToken);
      setTokenValid(true);
    } else {
      toast.error('Invalid reset link. Please request a new password reset.');
      navigate('/login');
    }
  }, [searchParams, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  const validatePassword = (password) => {
    const minLength = 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 6 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate new password
      const passwordError = validatePassword(formData.newPassword);
      if (passwordError) {
        toast.error(passwordError);
        return;
      }

      // Check if passwords match
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }

      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Password reset successfully! You can now log in with your new password.');
        navigate('/login');
      } else {
        toast.error(data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('An error occurred while resetting password');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (!tokenValid) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-background">
          <div className="reset-password-card">
            <div className="reset-password-header">
              <div className="logo-container">
                <svg xmlns="http://www.w3.org/2000/svg" width="120" height="36" viewBox="0 0 98 29.485">
                  <g transform="translate(-52 -39.515)">
                    <g transform="translate(-403 -140)">
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 182)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 197)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(476 179.515) rotate(45)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(470 197)"></rect>
                    </g>
                  </g>
                </svg>
              </div>
              <h1>Invalid Reset Link</h1>
              <p>The password reset link is invalid or has expired.</p>
            </div>

            <div className="reset-password-actions">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="back-to-login-button"
              >
                <FiArrowLeft />
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-background">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <div className="logo-container">
              <svg xmlns="http://www.w3.org/2000/svg" width="120" height="36" viewBox="0 0 98 29.485">
                <g transform="translate(-52 -39.515)">
                  <g transform="translate(-403 -140)">
                    <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 182)"></rect>
                    <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 197)"></rect>
                    <rect className="logo-square" width="12" height="12" rx="3" transform="translate(476 179.515) rotate(45)"></rect>
                    <rect className="logo-square" width="12" height="12" rx="3" transform="translate(470 197)"></rect>
                  </g>
                </g>
              </svg>
            </div>
            <h1>Reset Your Password</h1>
            <p>Enter your new password below</p>
          </div>

          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter your new password"
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  {showPasswords.new ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <div className="password-requirements">
                <p>Password must contain:</p>
                <ul>
                <li className={formData.newPassword.length >= 6 ? 'valid' : 'invalid'}>
                  At least 6 characters
                </li>
                  <li className={/[A-Z]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                    One lowercase letter
                  </li>
                  <li className={/\d/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                    One number
                  </li>
                  <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'valid' : 'invalid'}>
                    One special character
                  </li>
                </ul>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <div className="input-wrapper">
                <FiLock className="input-icon" />
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your new password"
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  {showPasswords.confirm ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {formData.confirmPassword && (
                <div className={`password-match ${formData.newPassword === formData.confirmPassword ? 'valid' : 'invalid'}`}>
                  {formData.newPassword === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="reset-button"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>

          <div className="reset-password-actions">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="back-to-login-button"
            >
              <FiArrowLeft />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 